import { FC, useEffect, useMemo, useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import { toast } from 'react-toastify'
import { uploadEquipmentLocationIcon, deleteEquipmentLocationIcon } from '../../core/_requests'
import type { EquipmentLocationIconDto } from '../../core/_models'

type Props = {
  visible: boolean
  locationCode: string
  currentIcon?: EquipmentLocationIconDto | null
  onClose: () => void
  onUpdated: (next: EquipmentLocationIconDto | null) => void
}

const MAX_BYTES = 5 * 1024
const DEFAULT_ICON = '/media/icons/duotune/technology/teh005.svg'
const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '')

const resolveIconUrl = (path?: string | null) => {
  if (!path) return null
  if (/^https?:\/\//i.test(path)) return path
  if (path.startsWith('/uploads/')) return `${API_ORIGIN}${path}`
  return `${API_ORIGIN}/${path.replace(/^\/+/, '')}`
}

export const EquipmentIconModal: FC<Props> = ({ visible, locationCode, currentIcon, onClose, onUpdated }) => {
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [removing, setRemoving] = useState(false)

  useEffect(() => {
    if (!visible) return
    setFile(null)
    setPreviewUrl(null)
  }, [visible])

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const activePreview = previewUrl || resolveIconUrl(currentIcon?.iconUrl) || null

  const sizeLabel = useMemo(() => {
    if (!file) return null
    return `${(file.size / 1024).toFixed(1)} KB`
  }, [file])

  const handleFileChange = (value: File | null) => {
    if (!value) {
      setFile(null)
      setPreviewUrl(null)
      return
    }
    if (value.size > MAX_BYTES) {
      toast.error('Icon must be 5 KB or smaller', { position: 'top-center' })
      return
    }
    if (!value.type.startsWith('image/')) {
      toast.error('Only image files are allowed', { position: 'top-center' })
      return
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setFile(value)
    setPreviewUrl(URL.createObjectURL(value))
  }

  const handleUpload = async () => {
    if (!file) return
    setUploading(true)
    try {
      const updated = await uploadEquipmentLocationIcon(locationCode, file)
      toast.success('Equipment icon updated', { position: 'top-center' })
      onUpdated({
        ...updated,
        iconUrl: resolveIconUrl(updated.iconUrl) || '',
      })
      onClose()
    } catch (error: any) {
      toast.error('Failed to upload icon', { position: 'top-center' })
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async () => {
    if (!currentIcon?.iconUrl) return
    setRemoving(true)
    try {
      await deleteEquipmentLocationIcon(locationCode)
      toast.success('Custom icon removed', { position: 'top-center' })
      onUpdated(null)
      onClose()
    } catch (error: any) {
      toast.error('Failed to remove icon', { position: 'top-center' })
    } finally {
      setRemoving(false)
    }
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1050 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content bg-white text-dark rounded-4 overflow-hidden'>
          <div className='modal-header py-4'>
            <h5 className='modal-title'>Equipment Icon</h5>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>
          <div className='modal-body'>
            <div className='mb-4'>
              <div className='text-muted fs-7'>Location Code</div>
              <div className='fw-bold fs-5 text-gray-900'>{locationCode}</div>
            </div>

            <div className='d-flex align-items-center gap-3 mb-4'>
              <div
                className='rounded-circle border border-dashed border-gray-300 d-flex align-items-center justify-content-center'
                style={{ width: 64, height: 64, background: '#f8f9fa' }}
              >
                {activePreview ? (
                  <img
                    src={activePreview}
                    alt='Equipment icon'
                    style={{ width: 40, height: 40, objectFit: 'contain' }}
                  />
                ) : (
                  <KTSVG path={DEFAULT_ICON} className='svg-icon-2x text-gray-500' />
                )}
              </div>
              <div>
                <div className='fw-semibold'>Current Icon</div>
                <div className='text-muted fs-7'>
                  {currentIcon?.iconUrl ? 'Custom icon is set.' : 'Using default icon.'}
                </div>
              </div>
            </div>

            <div className='mb-4'>
              <label className='form-label fw-semibold'>Upload / Replace</label>
              <input
                type='file'
                accept='image/*'
                className='form-control'
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
              <div className='text-muted fs-7 mt-2'>Max size 5 KB. PNG, JPG, SVG, or WEBP.</div>
              {sizeLabel && (
                <div className='text-muted fs-7 mt-1'>Selected: {sizeLabel}</div>
              )}
            </div>
          </div>
          <div className='modal-footer py-4 d-flex justify-content-between'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Close
            </button>
            <div className='d-flex gap-2'>
              {currentIcon?.iconUrl && (
                <button
                  type='button'
                  className='btn btn-light-danger'
                  onClick={handleRemove}
                  disabled={removing || uploading}
                >
                  {removing ? 'Removing...' : 'Remove Custom'}
                </button>
              )}
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleUpload}
                disabled={!file || uploading || removing}
              >
                {uploading ? 'Uploading...' : 'Save Icon'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EquipmentIconModal
